/* eslint-disable react/require-default-props */

import React, { PropsWithChildren } from 'react';
import Tooltip from 'components/Typography/Tooltip';
import Icon from 'components/DesignSystem/Icon';
import { IconType } from 'components/DesignSystem/Icon/icons';
import './index.scss';

export type TagType = 'primary' | 'active' | 'in_progress' | 'completed' | 'cancelled';
export type IconPlacement = 'leading' | 'trailing';

interface TagProps {
  type?: TagType
  icon?: IconType | undefined
  iconPlacement?: 'leading' | 'trailing'
  id?: string | undefined
  className?: string
  onClick?: (() => void) | undefined;
  backgroundColor?: string;
  /**
   * Torna o chip removível de forma acessível: com `onClick`, o ícone vira um
   * `<button type="button">` com este texto em `aria-label` e `title` (ex.: "Remover
   * filtro Status: Pronto"). Sem `icon`, usa o ícone `close`.
   */
  removeLabel?: string;
}

export default function Tag({
  children,
  type = 'primary',
  icon = undefined,
  iconPlacement = 'leading',
  id = undefined,
  className = '',
  onClick = undefined,
  backgroundColor = '',
  removeLabel = undefined,
}: PropsWithChildren<TagProps>) {
  const isRemovable = Boolean(onClick && removeLabel);
  const iconType: IconType | undefined = isRemovable ? (icon ?? 'close') : icon;

  const renderIcon = () => {
    if (!iconType) return null;
    if (isRemovable) {
      return (
        <button
          type="button"
          className="ds-tag-icon-button-wrapper ds-tag-clickable"
          aria-label={removeLabel}
          title={removeLabel}
          onClick={onClick}
        >
          <Icon iconType={iconType} size="small" className="ds-tag-icon" />
        </button>
      );
    }
    if (onClick) {
      return (
        <Icon
          iconType={iconType}
          size="small"
          className="ds-tag-icon ds-tag-clickable"
          onClick={onClick}
        />
      );
    }
    return <Icon iconType={iconType} size="small" className="ds-tag-icon" />;
  };

  return (
    <div
      id={id}
      className={`ds-tag-container ds-tag-${type}${backgroundColor ? ' ds-tag-data' : ''} ${className}`}
      // Cor de dado (ex.: status vindo do backend): o SCSS decide como pintá-la em cada tema.
      style={backgroundColor ? { '--ee-tag-color': backgroundColor } as React.CSSProperties : {}}
    >
      {iconPlacement === 'leading' && renderIcon()}
      <Tooltip>{children}</Tooltip>
      {iconPlacement === 'trailing' && renderIcon()}
    </div>
  );
}
